import { useRef, useState, type ChangeEvent } from 'react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { readFileAsDataUrl } from '@/lib/files';
import type { DriverDocument } from '@/types';

interface DocumentFieldProps {
  id: string;
  label: string;
  name: string;
  value: DriverDocument | null;
  onChange: (doc: DriverDocument | null) => void;
  accept: string;
  maxSizeMB: number;
  hint?: string;
  error?: string;
  labels: {
    upload: string;
    replace: string;
    remove: string;
    noFile: string;
    tooLarge: string;
    wrongType: string;
  };
}

function isImage(url: string | null): boolean {
  if (!url) return false;
  return url.startsWith('data:image') || /\.(png|jpe?g|webp|gif)$/i.test(url) || url.includes('readdy.ai');
}

/** Document picker showing the file name, a thumbnail when possible, and replace/remove. */
export default function DocumentField({
  id,
  label,
  name,
  value,
  onChange,
  accept,
  maxSizeMB,
  hint,
  error,
  labels,
}: DocumentFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string | null>(null);

  const handleSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    setInternalError(null);
    const file = event.target.files?.[0] ?? null;
    if (!file) return;

    const allowed = accept
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (allowed.length && !allowed.includes(ext)) {
      setInternalError(labels.wrongType);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setInternalError(labels.tooLarge.replace('{size}', formatNumber(maxSizeMB)));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      onChange({ fileName: file.name, url: dataUrl });
    } catch {
      setInternalError(labels.wrongType);
    }
  };

  const shownError = error || internalError || undefined;
  const thumb = value && isImage(value.url) ? value.url : null;

  return (
    <div className="w-full">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-foreground-800">
        {label}
      </label>

      <div
        className={cn(
          'flex flex-wrap items-center gap-3 rounded-md border bg-background-50 px-3 py-2.5',
          shownError ? 'border-rose-300' : 'border-background-300',
        )}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100 text-foreground-500">
          {thumb ? (
            <img src={thumb} alt={value?.fileName ?? label} className="h-full w-full object-cover" />
          ) : (
            <i className="ri-file-text-line text-lg" aria-hidden="true" />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-foreground-700">
          {value ? value.fileName : <span className="text-foreground-400">{labels.noFile}</span>}
        </span>

        <input
          ref={inputRef}
          id={id}
          name={name}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={handleSelect}
        />

        {value ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-background-300 px-2.5 text-xs font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              {labels.replace}
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-rose-200 px-2.5 text-xs font-medium text-rose-600 hover:bg-rose-50 cursor-pointer"
            >
              <i className="ri-delete-bin-line" aria-hidden="true" />
              {labels.remove}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-secondary-200 bg-secondary-100 px-3 text-xs font-medium text-secondary-900 hover:bg-secondary-200 cursor-pointer"
          >
            <i className="ri-upload-2-line" aria-hidden="true" />
            {labels.upload}
          </button>
        )}
      </div>

      {shownError ? (
        <p className="mt-1 text-xs text-rose-600">{shownError}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-foreground-500">{hint}</p>
      ) : null}
    </div>
  );
}